'use client';

import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { CheckboxField, SelectField, TextAreaField, TextField } from '@/components/ui/Field';
import { Spinner } from '@/components/ui/Feedback';
import { Icon } from '@/components/ui/Icon';
import { useLeadSubmission } from '@/hooks/useLeadSubmission';
import { formatPrice } from '@/lib/format';
import { hasErrors, isBlank, isEmail, isPhone, messages, type ValidationErrors } from '@/lib/validation';
import { PURCHASABLE_PRODUCTS, getProduct } from '@/data/products';

import { SubmissionError, SubmissionSuccess } from './FormFeedback';
import { MarketingOptIn, NO_MARKETING_OPT_IN, type MarketingOptInValue } from './MarketingOptIn';

interface OrderFormValues {
  productId: string;
  quantity: string;
  name: string;
  business: string;
  email: string;
  phone: string;
  location: string;
  message: string;
}

const PRODUCT_OPTIONS = PURCHASABLE_PRODUCTS.map((product) => ({
  value: product.id,
  label: `${product.name} — ${formatPrice(product.priceUsd)} + shipping`,
}));

const QUANTITY_OPTIONS = ['1', '2', '3', '4', '5', '6-10', '10+'].map((value) => ({ value, label: value }));

function validate(values: OrderFormValues): ValidationErrors<OrderFormValues> {
  return {
    productId: isBlank(values.productId) ? messages.required : undefined,
    name: isBlank(values.name) ? messages.required : undefined,
    email: isBlank(values.email) ? messages.required : isEmail(values.email) ? undefined : messages.email,
    phone: isBlank(values.phone) ? messages.required : isPhone(values.phone) ? undefined : messages.phone,
    location: isBlank(values.location) ? messages.required : undefined,
  };
}

export function OrderForm({ initialProductId }: { initialProductId?: string }) {
  const [values, setValues] = useState<OrderFormValues>({
    productId: initialProductId && getProduct(initialProductId) ? initialProductId : PURCHASABLE_PRODUCTS[0].id,
    quantity: '1',
    name: '',
    business: '',
    email: '',
    phone: '',
    location: '',
    message: '',
  });
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
  const [errors, setErrors] = useState<ValidationErrors<OrderFormValues>>({});
  const [marketing, setMarketing] = useState<MarketingOptInValue>(NO_MARKETING_OPT_IN);
  const submission = useLeadSubmission();

  const product = getProduct(values.productId);

  // Quantity can be a range ("6-10"), so the estimate uses the lower bound.
  const unitCount = useMemo(() => {
    const parsed = Number.parseInt(values.quantity, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }, [values.quantity]);

  const addOnTotal = useMemo(() => {
    if (!product) return 0;
    return product.addOns
      .filter((addOn) => selectedAddOns.includes(addOn.id))
      .reduce((total, addOn) => total + addOn.priceUsd, 0);
  }, [product, selectedAddOns]);

  const estimate = product ? (product.priceUsd + addOnTotal) * unitCount : 0;

  const setValue = <K extends keyof OrderFormValues>(key: K, value: OrderFormValues[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
    if (key === 'productId') setSelectedAddOns([]);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (hasErrors(nextErrors) || !product) return;

    const addOnNames = product.addOns
      .filter((addOn) => selectedAddOns.includes(addOn.id))
      .map((addOn) => addOn.name)
      .join(', ');

    await submission.submit({
      marketing,
      type: 'order',
      name: values.name,
      email: values.email,
      phone: values.phone,
      business: values.business,
      location: values.location,
      message: values.message,
      details: {
        product: product.name,
        productId: product.id,
        quantity: values.quantity,
        addOns: addOnNames || 'None',
        estimatedHardwareTotal: formatPrice(estimate),
        shipping: 'To be quoted',
      },
    });
  };

  if (submission.status === 'success') {
    return (
      <SubmissionSuccess
        title="Order request received"
        description="Thanks — we have your details. We will confirm availability, quote shipping to your location and send you a payment link. Nothing is charged until you approve the total."
        onReset={submission.reset}
        resetLabel="Place another order"
      />
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="grid gap-8 lg:grid-cols-[1.35fr_0.65fr] lg:items-start">
      <Card className="flex flex-col gap-6">
        <div className="grid gap-6 sm:grid-cols-2">
          <SelectField
            label="Model"
            required
            className="sm:col-span-2"
            options={PRODUCT_OPTIONS}
            value={values.productId}
            error={errors.productId}
            onChange={(event) => setValue('productId', event.target.value)}
          />

          <SelectField
            label="How many units?"
            options={QUANTITY_OPTIONS}
            value={values.quantity}
            onChange={(event) => setValue('quantity', event.target.value)}
          />

          <TextField
            label="Business name"
            placeholder="Sunrise Convenience"
            autoComplete="organization"
            value={values.business}
            onChange={(event) => setValue('business', event.target.value)}
          />
        </div>

        {product && product.addOns.length > 0 ? (
          <fieldset className="border-line-strong/60 bg-ink/40 flex flex-col gap-3 rounded-2xl border p-4">
            <legend className="text-content-subtle px-1 text-xs font-semibold tracking-wider uppercase">
              Optional add-ons
            </legend>
            {product.addOns.map((addOn) => (
              <CheckboxField
                key={addOn.id}
                checked={selectedAddOns.includes(addOn.id)}
                onChange={(checked) =>
                  setSelectedAddOns((current) =>
                    checked ? [...current, addOn.id] : current.filter((id) => id !== addOn.id),
                  )
                }
                label={
                  <>
                    <span className="text-white">{addOn.name}</span> — {formatPrice(addOn.priceUsd)}
                    <span className="text-content-subtle block text-xs">{addOn.description}</span>
                  </>
                }
              />
            ))}
          </fieldset>
        ) : null}

        <div className="grid gap-6 sm:grid-cols-2">
          <TextField
            label="Your name"
            required
            autoComplete="name"
            value={values.name}
            error={errors.name}
            onChange={(event) => setValue('name', event.target.value)}
          />
          <TextField
            label="Email"
            type="email"
            required
            autoComplete="email"
            inputMode="email"
            value={values.email}
            error={errors.email}
            onChange={(event) => setValue('email', event.target.value)}
          />
          <TextField
            label="Phone"
            type="tel"
            required
            autoComplete="tel"
            inputMode="tel"
            value={values.phone}
            error={errors.phone}
            onChange={(event) => setValue('phone', event.target.value)}
          />
          <TextField
            label="Delivery city & state"
            required
            hint="Used to quote shipping."
            autoComplete="address-level2"
            value={values.location}
            error={errors.location}
            onChange={(event) => setValue('location', event.target.value)}
          />
        </div>

        <TextAreaField
          label="Anything else we should know?"
          rows={4}
          placeholder="Venue type, installation timing, which platform you want promoted…"
          value={values.message}
          onChange={(event) => setValue('message', event.target.value)}
        />

        <MarketingOptIn value={marketing} onChange={setMarketing} />

        <SubmissionError submission={submission} />
      </Card>

      <Card className="lg:sticky lg:top-24" highlighted>
        <h2 className="text-xl text-white">Order summary</h2>

        <dl className="border-line/70 mt-5 flex flex-col gap-3 border-b pb-5 text-sm">
          <div className="flex items-start justify-between gap-4">
            <dt className="text-content-muted">Model</dt>
            <dd className="text-right text-white">{product?.name ?? '—'}</dd>
          </div>
          <div className="flex items-start justify-between gap-4">
            <dt className="text-content-muted">Unit price</dt>
            <dd className="text-white">{product ? formatPrice(product.priceUsd) : '—'}</dd>
          </div>
          {addOnTotal > 0 ? (
            <div className="flex items-start justify-between gap-4">
              <dt className="text-content-muted">Add-ons (per unit)</dt>
              <dd className="text-white">{formatPrice(addOnTotal)}</dd>
            </div>
          ) : null}
          <div className="flex items-start justify-between gap-4">
            <dt className="text-content-muted">Quantity</dt>
            <dd className="text-white">{values.quantity}</dd>
          </div>
          <div className="flex items-start justify-between gap-4">
            <dt className="text-content-muted">LaunchPad app</dt>
            <dd className="text-success font-semibold">Free</dd>
          </div>
          <div className="flex items-start justify-between gap-4">
            <dt className="text-content-muted">Setup & installation</dt>
            <dd className="text-success font-semibold">Free</dd>
          </div>
          <div className="flex items-start justify-between gap-4">
            <dt className="text-content-muted">Shipping</dt>
            <dd className="text-content-subtle">Quoted separately</dd>
          </div>
        </dl>

        <div className="mt-5 flex items-baseline justify-between gap-4">
          <span className="text-content-muted text-sm">Estimated hardware total</span>
          <span className="font-display text-3xl font-bold text-white">{formatPrice(estimate)}</span>
        </div>
        <p className="text-content-subtle mt-2 text-xs leading-relaxed">
          Estimate excludes shipping and any applicable tax. We confirm the final total before any payment is taken.
        </p>

        <Button type="submit" fullWidth size="lg" className="mt-6" disabled={submission.status === 'submitting'}>
          {submission.status === 'submitting' ? (
            <>
              <Spinner /> Sending…
            </>
          ) : (
            <>
              Request this order
              <Icon name="arrow" className="h-5 w-5" />
            </>
          )}
        </Button>
      </Card>
    </form>
  );
}
